<script setup lang="ts">
/**
 * Colour palette panel: an optional reset row, the ten standard colours with
 * three lighter and two darker steps each, a grayscale row, the recently used
 * colours, and „Weitere Farben …“ — the browser's own picker for anything
 * else. Emits `select` with `#RRGGBB`, or `reset`.
 *
 * Every text comes from the kit messages (`common.color_picker.*`, German
 * without a host); a label prop wins over them.
 *
 * Only the panel — put it in a popover, a menu or a ribbon dropdown; for a
 * form field with a swatch and a hex text use NxColorInput. Arrow keys move
 * through the grid; the current `value` is marked.
 */
import Check from 'lucide-vue-next/dist/esm/icons/check.js'; import Palette from 'lucide-vue-next/dist/esm/icons/palette.js'; import RotateCcw from 'lucide-vue-next/dist/esm/icons/rotate-ccw.js';
import { computed, inject, useTemplateRef } from 'vue';
import { i18nInjectionKey } from '../internal';
import { NX_GRAYSCALE_COLORS, normalizeHex, standardColorGrid, type NxSwatch } from './palette';

const props = withDefaults(
  defineProps<{
    /** Current colour, marked in the grid. */
    value?: string | null;
    /** Most recent first; shown as a row when not empty. */
    recent?: readonly string[];
    /** Label of the reset row („Automatisch“, „Keine Füllung“); no row without it. */
    resetLabel?: string;
    /** Wins over `common.color_picker.recent`. */
    recentLabel?: string;
    /** Wins over `common.color_picker.standard`. */
    standardLabel?: string;
    /** Wins over `common.color_picker.more`. */
    moreLabel?: string;
    /** Accessible name of the panel; wins over `common.color_picker.aria_label`. */
    ariaLabel?: string;
  }>(),
  {
    value: null,
    recent: () => [],
    resetLabel: undefined,
    recentLabel: undefined,
    standardLabel: undefined,
    moreLabel: undefined,
    ariaLabel: undefined,
  }
);

const emit = defineEmits<{
  select: [hex: string];
  reset: [];
}>();

// The host's translation under `common.color_picker.*`, else the German
// fallback — a host without kit messages hands the key back.
const i18n = inject(i18nInjectionKey, null);
function kitText(key: string, fallback: string, params?: Record<string, string | number>): string {
  const full = `common.color_picker.${key}`;
  const text = params ? i18n?.t(full, params) : i18n?.t(full);
  if (text && text !== full) return text;
  return Object.entries(params ?? {}).reduce(
    (out, [name, value]) => out.replace(`{${name}}`, String(value)),
    fallback
  );
}
const labels = computed(() => {
  void i18n?.locale?.value;
  return {
    standard: props.standardLabel ?? kitText('standard', 'Standardfarben'),
    recent: props.recentLabel ?? kitText('recent', 'Zuletzt verwendet'),
    more: props.moreLabel ?? kitText('more', 'Weitere Farben …'),
    aria: props.ariaLabel ?? kitText('aria_label', 'Farbe wählen'),
  };
});

/** Column-major, labels in the host's language (re-read on a locale change). */
const grid = computed<NxSwatch[][]>(() => {
  void i18n?.locale?.value;
  // The step templates keep their placeholders for `standardColorGrid`.
  const keep = { name: '{name}', pct: '{pct}' };
  return standardColorGrid(
    kitText('lighter', '{name}, {pct} % heller', keep),
    kitText('darker', '{name}, {pct} % dunkler', keep),
    (color) => kitText(`colors.${color.key}`, color.name)
  );
});
/** Row-major for rendering: row 0 is the base colours, then the tint rows. */
const rows = computed<NxSwatch[][]>(() =>
  grid.value[0]!.map((_, r) => grid.value.map((col) => col[r]!))
);
const gray = computed<NxSwatch[]>(() => {
  void i18n?.locale?.value;
  return NX_GRAYSCALE_COLORS.map((hex, i, all) => ({
    hex,
    label:
      i === 0
        ? kitText('black', 'Schwarz')
        : i === all.length - 1
          ? kitText('white', 'Weiß')
          : kitText('gray', 'Grau {pct} %', { pct: Math.round((i / 9) * 100) }),
  }));
});
/** Recent colours in any hex spelling; named like their swatch when they are one. */
const recentSwatches = computed<NxSwatch[]>(() => {
  const named = new Map([...grid.value.flat(), ...gray.value].map((s) => [s.hex, s.label]));
  return props.recent
    .map((hex) => normalizeHex(hex, true))
    .filter((hex): hex is string => hex !== null)
    .slice(0, 10)
    .map((hex) => ({ hex, label: named.get(hex) ?? hex }));
});
const current = computed(() => normalizeHex(props.value, true));

const panel = useTemplateRef<HTMLElement>('panel');

function pick(hex: string) {
  const n = normalizeHex(hex, true);
  if (n) emit('select', n);
}

function onNative(event: Event) {
  pick((event.target as HTMLInputElement).value);
}

/** Arrow keys walk the swatch buttons as one grid of ten columns. */
function onKeydown(event: KeyboardEvent) {
  const keys = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End'];
  if (!keys.includes(event.key)) return;
  const swatches = [...(panel.value?.querySelectorAll<HTMLButtonElement>('[data-swatch]') ?? [])];
  const i = swatches.indexOf(document.activeElement as HTMLButtonElement);
  if (i < 0) return;
  const cols = 10;
  const next = {
    ArrowRight: i + 1,
    ArrowLeft: i - 1,
    ArrowDown: i + cols,
    ArrowUp: i - cols,
    Home: 0,
    End: swatches.length - 1,
  }[event.key]!;
  const target = swatches[Math.max(0, Math.min(swatches.length - 1, next))];
  if (!target) return;
  event.preventDefault();
  target.focus();
}
</script>

<template>
  <div
    ref="panel"
    class="nx-color-swatch-picker"
    role="group"
    :aria-label="labels.aria"
    @keydown="onKeydown"
  >
    <button
      v-if="props.resetLabel"
      type="button"
      class="nx-color-swatch-picker__row-action"
      data-testid="nx-color-reset"
      @click="emit('reset')"
    >
      <RotateCcw class="nxt-icon-sm" aria-hidden="true" />
      {{ props.resetLabel }}
    </button>

    <div class="nx-color-swatch-picker__heading">{{ labels.standard }}</div>
    <div class="nx-color-swatch-picker__grid">
      <template v-for="(row, r) in rows" :key="r">
        <button
          v-for="swatch in row"
          :key="`${r}-${swatch.hex}`"
          type="button"
          class="nx-color-swatch-picker__swatch"
          :class="{ 'is-base': r === 0, 'is-current': current === swatch.hex }"
          :style="{ background: swatch.hex }"
          :title="swatch.label"
          :aria-label="swatch.label"
          :aria-pressed="current === swatch.hex"
          data-swatch
          @click="pick(swatch.hex)"
        >
          <Check
            v-if="current === swatch.hex"
            class="nx-color-swatch-picker__check"
            aria-hidden="true"
          />
        </button>
      </template>
    </div>
    <div class="nx-color-swatch-picker__grid is-gray">
      <button
        v-for="swatch in gray"
        :key="swatch.hex"
        type="button"
        class="nx-color-swatch-picker__swatch"
        :class="{ 'is-current': current === swatch.hex }"
        :style="{ background: swatch.hex }"
        :title="swatch.label"
        :aria-label="swatch.label"
        :aria-pressed="current === swatch.hex"
        data-swatch
        @click="pick(swatch.hex)"
      >
        <Check
          v-if="current === swatch.hex"
          class="nx-color-swatch-picker__check"
          aria-hidden="true"
        />
      </button>
    </div>

    <template v-if="recentSwatches.length">
      <div class="nx-color-swatch-picker__heading">{{ labels.recent }}</div>
      <div class="nx-color-swatch-picker__grid" data-testid="nx-color-recent">
        <button
          v-for="swatch in recentSwatches"
          :key="swatch.hex"
          type="button"
          class="nx-color-swatch-picker__swatch"
          :class="{ 'is-current': current === swatch.hex }"
          :style="{ background: swatch.hex }"
          :title="swatch.label"
          :aria-label="swatch.label"
          :aria-pressed="current === swatch.hex"
          data-swatch
          @click="pick(swatch.hex)"
        >
          <Check
            v-if="current === swatch.hex"
            class="nx-color-swatch-picker__check"
            aria-hidden="true"
          />
        </button>
      </div>
    </template>

    <label class="nx-color-swatch-picker__row-action">
      <Palette class="nxt-icon-sm" aria-hidden="true" />
      {{ labels.more }}
      <input
        class="nx-color-swatch-picker__native"
        type="color"
        :value="(current ?? '#000000').slice(0, 7).toLowerCase()"
        data-testid="nx-color-native"
        @change="onNative"
      />
    </label>
  </div>
</template>

<style scoped>
.nx-color-swatch-picker {
  --swatch: calc(var(--nxt-size-unit) * 1.25);

  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  width: max-content;
  padding: var(--nxt-space-xs);
}
.nx-color-swatch-picker__heading {
  margin-block-start: var(--nxt-space-2xs);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
}
.nx-color-swatch-picker__grid {
  display: grid;
  grid-template-columns: repeat(10, var(--swatch));
  gap: calc(var(--nxt-hairline) * 3);
}
.nx-color-swatch-picker__grid.is-gray {
  margin-block-start: var(--nxt-space-2xs);
}
.nx-color-swatch-picker__swatch {
  position: relative;
  display: grid;
  place-items: center;
  width: var(--swatch);
  height: var(--swatch);
  padding: 0;
  border: var(--nxt-hairline) solid color-mix(in srgb, var(--nxt-fg) 14%, transparent);
  border-radius: calc(var(--nxt-hairline) * 3);
  cursor: pointer;
  transition: transform var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-color-swatch-picker__swatch.is-base {
  margin-block-end: calc(var(--nxt-hairline) * 3);
}
.nx-color-swatch-picker__swatch:hover {
  z-index: 1;
  transform: scale(1.18);
}
.nx-color-swatch-picker__swatch:focus-visible {
  z-index: 1;
  outline: none;
  box-shadow:
    0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-surface-raised-bg),
    0 0 0 calc(var(--nxt-hairline) * 4) var(--nxt-focus-ring);
}
.nx-color-swatch-picker__swatch.is-current {
  box-shadow:
    0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-surface-raised-bg),
    0 0 0 calc(var(--nxt-hairline) * 3.5) var(--nxt-fg);
}
.nx-color-swatch-picker__check {
  width: 70%;
  height: 70%;
  color: white;
  filter: drop-shadow(0 0 calc(var(--nxt-hairline) * 1.5) rgb(0 0 0 / 70%));
}
.nx-color-swatch-picker__row-action {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg);
  font: inherit;
  font-size: var(--nxt-font-size-sm);
  text-align: start;
  cursor: pointer;
}
.nx-color-swatch-picker__row-action:hover,
.nx-color-swatch-picker__row-action:focus-within {
  background: var(--nxt-table-row-hover-bg);
}
.nx-color-swatch-picker__native {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}
@media (prefers-reduced-motion: reduce) {
  .nx-color-swatch-picker__swatch {
    transition: none;
  }
}
@media (forced-colors: active) {
  .nx-color-swatch-picker__swatch {
    forced-color-adjust: none;
  }
}
</style>
